import React, { RefObject } from 'react';

import { getTimelineMonthDividers } from './get-timeline-month-dividers';
import { getTimelineViewportRange } from './get-timeline-viewport-range';
import { getTimelineWeekendBands } from './get-timeline-weekend-bands';
import * as Styled from './goal-timeline.style';
import { PositionedTimelineColumn } from './types';
import { useTimelineScrollLeft } from './use-timeline-scroll-left';

export type TimelineGridBackgroundProps = {
  columns: PositionedTimelineColumn[];
  hideColumnBorders: boolean;
  weekendBands: ReturnType<typeof getTimelineWeekendBands>;
  monthDividers: ReturnType<typeof getTimelineMonthDividers>;
  scrollRef: RefObject<HTMLDivElement>;
  viewportWidth: number;
  syncTo: number;
};

// Renders only the grid chrome (weekend shading, column borders, month dividers)
// that intersects the visible horizontal window — the ±12-year canvas holds
// hundreds of columns, but a viewport shows a handful. Tracks scroll itself so a
// scroll re-renders this overlay, not the whole timeline (sidebar + rows).
export const TimelineGridBackground: React.FC<TimelineGridBackgroundProps> =
  React.memo(
    ({
      columns,
      hideColumnBorders,
      weekendBands,
      monthDividers,
      scrollRef,
      viewportWidth,
      syncTo,
    }) => {
      const scrollLeft = useTimelineScrollLeft(scrollRef, syncTo);
      const { start, end } = getTimelineViewportRange(
        scrollLeft,
        viewportWidth,
        viewportWidth,
      );

      const visibleColumns = columns.filter(
        (column) => column.left < end && column.left + column.width > start,
      );
      const visibleWeekendBands = weekendBands.filter(
        (band) => band.left < end && band.left + band.width > start,
      );
      const visibleMonthDividers = monthDividers.filter(
        (divider) => divider.left >= start && divider.left <= end,
      );

      return (
        <>
          {!!visibleWeekendBands.length && (
            <Styled.WeekendContainer>
              {visibleWeekendBands.map((band) => (
                <Styled.WeekendBand
                  key={band.key}
                  $left={band.left}
                  $width={band.width}
                />
              ))}
            </Styled.WeekendContainer>
          )}

          <Styled.MonthContainer>
            {visibleColumns.map((column) => (
              <Styled.MonthBg
                key={column.key}
                $left={column.left}
                $width={column.width}
                $hideBorder={hideColumnBorders}
              />
            ))}
          </Styled.MonthContainer>

          {!!visibleMonthDividers.length && (
            <Styled.MonthDividerContainer>
              {visibleMonthDividers.map((divider) => (
                <Styled.MonthDivider key={divider.key} $left={divider.left} />
              ))}
            </Styled.MonthDividerContainer>
          )}
        </>
      );
    },
  );
